import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  SubscriptionPlan,
  SubscriptionPlansResponse,
  SubscribePlanRequest,
  PlanConfigData,
  CountryData,
  ChangePaymentMethodRequest,
  UserActiveStatsResponse,
  ValidateUpdateSubscriptionPlanResponse,
  ValidateUpdateSubscriptionPlanRequest,
  UpdateSubscriptionPlanRequest,
  UpdateSubscriptionPlanResponse,
  SubscriptionInvoice,
  InvoiceHistoryListResponse,
  ValidatePromotionResponse,
  ValidatePromotionRequest,
  SubscriptionPlanPaymentResponse,
  SubscriptionPlanPaymentRequest,
  GetNewBillingInfoResponse,
  GetNewBillingInfoRequest,
} from '../../types';
import {
  SUBSCRIPTION_PLAN_SCREEN_PAGES,
  SubscriptionPlanScreenPage,
} from '../../constants/enums';

const mainSlice = createSlice({
  name: 'main',
  initialState: {
    loading: false,
    error: null,
    currentScreenPage:
      SUBSCRIPTION_PLAN_SCREEN_PAGES.PLAN_LIST as SubscriptionPlanScreenPage,
    previousScreenPage: null as SubscriptionPlanScreenPage | null,
    // Choose plan states
    planConfig: null as PlanConfigData | null,
    // Subscribe plan states
    subscribePlanData: null as SubscribePlanRequest | null,
    subscribing: false,
    subscribeError: null,
    subscriptionResult: null as any,
    /* #region Invoice History */
    // Invoice history states
    invoiceHistory: [] as SubscriptionInvoice[],
    invoicePagination: {
      currentPage: 1,
      limit: 10,
      totalCount: 0,
      totalPages: 1,
      hasNextPage: false,
      hasPrevPage: false,
      nextPage: null as number | null,
      prevPage: null as number | null,
    },
    invoiceLoading: false,
    invoiceError: undefined as string | undefined,
    /* #endregion Invoice History */
    // Promotion validation states
    validatingPromotion: false,
    validatePromotionError: null,
    validatePromotionResult: null as ValidatePromotionResponse | null,
    // Subscription details states
    showChangePaymentMethod: false,
    showChangePlan: null as boolean | null,
    subscriptions: [] as SubscriptionPlan[],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      hasNextPage: false,
      hasPrevPage: false,
    },
    userActiveStats: null as UserActiveStatsResponse | null,
    // Payment method change states
    cardConnectHost: null as string | null,
    countries: [] as CountryData[],
    changingPaymentMethod: false,
    changePaymentMethodError: null,
    changePaymentMethodSuccess: false,

    // Validate update subscription plan states
    validateUpdateSubscriptionPlanError: null as string | null,
    validateUpdateSubscriptionPlanResult:
      null as ValidateUpdateSubscriptionPlanResponse | null,

    // Update subscription plan
    updatingSubscriptionPlan: false,
    updateSubscriptionPlanRequestData:
      null as UpdateSubscriptionPlanRequest | null,
    updateSubscriptionPlanError: null as string | null,
    updateSubscriptionPlanResult: null as UpdateSubscriptionPlanResponse | null,

    // subscription plan payment
    subscriptionPlanPaymentResult:
      null as SubscriptionPlanPaymentResponse | null,
    subscriptionPlanPaymentError: null as string | null,

    // new billing info for suspended_due continue option
    newBillingInfo: null as GetNewBillingInfoResponse | null,
    loadingNewBillingInfo: false,
    newBillingInfoError: null as string | null,
  },
  reducers: {
    setCurrentScreenPage: (
      state,
      action: PayloadAction<SubscriptionPlanScreenPage>
    ) => {
      state.previousScreenPage = state.currentScreenPage;
      state.currentScreenPage = action.payload;
    },
    goBackScreenPage: (state) => {
      state.currentScreenPage =
        state.previousScreenPage || SUBSCRIPTION_PLAN_SCREEN_PAGES.PLAN_LIST;
      state.previousScreenPage = null;
    },
    // get plan config
    getPlanConfigRequest: (state) => {
      state.loading = true;
    },
    getPlanConfigSuccess: (state, action: PayloadAction<PlanConfigData>) => {
      state.loading = false;
      state.planConfig = action.payload;
    },
    getPlanConfigFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // invoice history
    getInvoiceHistoryRequest: (
      state,
      action: PayloadAction<{ page: number; limit: number }>
    ) => {
      state.invoiceLoading = true;
    },
    getInvoiceHistorySuccess: (
      state,
      action: PayloadAction<InvoiceHistoryListResponse>
    ) => {
      state.invoiceLoading = false;
      state.invoiceHistory = action.payload.items;
      if (action.payload.pagination) {
        state.invoicePagination = action.payload.pagination;
      }
    },
    getInvoiceHistoryFailure: (state, action: PayloadAction<string>) => {
      state.invoiceLoading = false;
      state.invoiceError = action.payload;
    },
    // get subscriptions
    getSubscriptionsRequest: (
      state,
      _action: PayloadAction<{
        page?: number;
        limit?: number;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
      }>
    ) => {
      state.loading = true;
    },
    getSubscriptionsSuccess: (
      state,
      action: PayloadAction<SubscriptionPlansResponse>
    ) => {
      state.loading = false;
      state.subscriptions = action.payload.subscriptionPlans || [];
      state.pagination = action.payload.pagination || state.pagination;
    },
    getSubscriptionsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    setShowChangePaymentMethod: (state, action: PayloadAction<boolean>) => {
      state.showChangePaymentMethod = action.payload;
    },
    setShowChangePlan: (state, action: PayloadAction<boolean | null>) => {
      state.loading = false;
      state.showChangePlan = action.payload;
      state.validateUpdateSubscriptionPlanResult = null;
      state.updateSubscriptionPlanResult = null;
    },
    // subscribe plan
    subscribePlanRequest: (
      state,
      action: PayloadAction<SubscribePlanRequest>
    ) => {
      state.loading = true;
      state.subscribing = true;
      state.subscribeError = null;
      state.subscribePlanData = action.payload;
    },
    subscribePlanSuccess: (state, action) => {
      state.loading = false;
      state.subscribing = false;
      state.subscriptionResult = action.payload;
    },
    subscribePlanFailure: (state, action) => {
      state.loading = false;
      state.subscribing = false;
      state.subscribeError = action.payload;
    },
    resetSubscribePlanState: (state) => {
      state.loading = false;
      state.subscribing = false;
      state.subscribeError = null;
      state.subscriptionResult = null;
    },
    // validate promotion
    validatePromotionRequest: (
      state,
      _action: PayloadAction<ValidatePromotionRequest>
    ) => {
      state.validatingPromotion = true;
      state.validatePromotionError = null;
      state.validatePromotionResult = null;
    },
    validatePromotionSuccess: (
      state,
      action: PayloadAction<ValidatePromotionResponse>
    ) => {
      state.validatingPromotion = false;
      state.validatePromotionResult = action.payload;
    },
    validatePromotionFailure: (state, action) => {
      state.validatingPromotion = false;
      state.validatePromotionError = action.payload;
      state.validatePromotionResult = null;
    },
    resetPromotionValidation: (state) => {
      state.loading = false;
      state.validatingPromotion = false;
      state.validatePromotionError = null;
      state.validatePromotionResult = null;
    },
    // CardConnect host
    getCardConnectHostRequest: (state) => {
      state.loading = true;
    },
    getCardConnectHostSuccess: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.cardConnectHost = action.payload;
      console.log('getCardConnectHostSuccess', state.cardConnectHost);
    },
    getCardConnectHostFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      console.log('getCardConnectHostFailure', state.error);
    },
    // Countries
    getAllCountriesRequest: (state) => {
      state.loading = true;
    },
    getAllCountriesSuccess: (state, action: PayloadAction<CountryData[]>) => {
      state.loading = false;
      state.countries = action.payload;
    },
    getAllCountriesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Change payment method
    changePaymentMethodRequest: (
      state,
      _action: PayloadAction<ChangePaymentMethodRequest>
    ) => {
      state.loading = true;
      state.changingPaymentMethod = true;
      state.changePaymentMethodError = null;
      state.changePaymentMethodSuccess = false;
    },
    changePaymentMethodSuccess: (state) => {
      state.changingPaymentMethod = false;
      state.changePaymentMethodSuccess = true;
      state.loading = false;
    },
    changePaymentMethodFailure: (state, action) => {
      state.changingPaymentMethod = false;
      state.changePaymentMethodError = action.payload;
      state.loading = false;
    },
    resetChangePaymentMethodState: (state) => {
      state.changingPaymentMethod = false;
      state.changePaymentMethodError = null;
      state.changePaymentMethodSuccess = false;
      state.loading = false;
    },
    // Get user active stats
    getUserActiveStatsRequest: (state) => {
      state.loading = true;
    },
    getUserActiveStatsSuccess: (
      state,
      action: PayloadAction<UserActiveStatsResponse>
    ) => {
      state.loading = false;
      state.userActiveStats = action.payload;
      console.log('getUserActiveStatsSuccess', state.userActiveStats);
    },
    getUserActiveStatsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // validate update subscription plan
    validateUpdateSubscriptionPlanRequest: (
      state,
      _action: PayloadAction<ValidateUpdateSubscriptionPlanRequest>
    ) => {
      state.loading = true;
      state.validateUpdateSubscriptionPlanError = null;
      state.validateUpdateSubscriptionPlanResult = null;
    },
    validateUpdateSubscriptionPlanSuccess: (
      state,
      action: PayloadAction<ValidateUpdateSubscriptionPlanResponse>
    ) => {
      state.loading = false;
      state.validateUpdateSubscriptionPlanResult = action.payload;
    },
    validateUpdateSubscriptionPlanFailure: (
      state,
      action: PayloadAction<string>
    ) => {
      state.loading = false;
      state.validateUpdateSubscriptionPlanError = action.payload;
    },
    resetValidateUpdateSubscriptionPlan: (state) => {
      state.loading = false;
      state.validateUpdateSubscriptionPlanResult = null;
      state.validateUpdateSubscriptionPlanError = null;
    },
    // update subscription plan
    updateSubscriptionPlanRequest: (
      state,
      action: PayloadAction<UpdateSubscriptionPlanRequest>
    ) => {
      state.loading = true;
      state.updatingSubscriptionPlan = true;
      state.updateSubscriptionPlanError = null;
      state.updateSubscriptionPlanResult = null;
      state.updateSubscriptionPlanRequestData = action.payload;
    },
    updateSubscriptionPlanSuccess: (
      state,
      action: PayloadAction<UpdateSubscriptionPlanResponse>
    ) => {
      state.loading = false;
      state.updatingSubscriptionPlan = false;
      state.updateSubscriptionPlanResult = action.payload;
    },
    updateSubscriptionPlanFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.updatingSubscriptionPlan = false;
      state.updateSubscriptionPlanError = action.payload;
    },
    resetUpdateSubscriptionPlan: (state) => {
      state.loading = false;
      state.updateSubscriptionPlanError = null;
      state.updateSubscriptionPlanResult = null;
      // state.updateSubscriptionPlanRequestData = null;
    },
    // subscription payment for failed & suspended accounts
    subscriptionPlanPaymentRequest: (
      state,
      _action: PayloadAction<SubscriptionPlanPaymentRequest>
    ) => {
      state.loading = true;
      state.subscriptionPlanPaymentResult = null;
    },
    subscriptionPlanPaymentSuccess: (
      state,
      action: PayloadAction<SubscriptionPlanPaymentResponse>
    ) => {
      state.loading = false;
      state.subscriptionPlanPaymentResult = action.payload;
    },
    subscriptionPlanPaymentFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.subscriptionPlanPaymentError = action.payload;
      state.subscriptionPlanPaymentResult = {
        success: false,
        message: action.payload,
      };
    },
    resetSubscriptionPlanPayment: (state) => {
      state.loading = false;
      state.subscriptionPlanPaymentResult = null;
      state.subscriptionPlanPaymentError = null;
    },
    getNewBillingInfoRequest: (
      state,
      _action: PayloadAction<GetNewBillingInfoRequest>
    ) => {
      state.loadingNewBillingInfo = true;
      state.newBillingInfoError = null;
    },
    getNewBillingInfoSuccess: (
      state,
      action: PayloadAction<GetNewBillingInfoResponse>
    ) => {
      state.loadingNewBillingInfo = false;
      state.newBillingInfo = action.payload;
    },
    getNewBillingInfoFailure: (state, action: PayloadAction<string>) => {
      state.loadingNewBillingInfo = false;
      state.newBillingInfoError = action.payload;
      state.newBillingInfo = null;
    },
    resetNewBillingInfo: (state) => {
      state.loadingNewBillingInfo = false;
      state.newBillingInfo = null;
      state.newBillingInfoError = null;
    },
  },
});

export const {
  setCurrentScreenPage,
  goBackScreenPage,
  getInvoiceHistoryRequest,
  getInvoiceHistorySuccess,
  getInvoiceHistoryFailure,
  getPlanConfigRequest,
  getPlanConfigSuccess,
  getPlanConfigFailure,
  getSubscriptionsRequest,
  getSubscriptionsSuccess,
  getSubscriptionsFailure,
  subscribePlanRequest,
  subscribePlanSuccess,
  subscribePlanFailure,
  resetSubscribePlanState,
  validatePromotionRequest,
  validatePromotionSuccess,
  validatePromotionFailure,
  resetPromotionValidation,
  setShowChangePaymentMethod,
  setShowChangePlan,
  getCardConnectHostRequest,
  getCardConnectHostSuccess,
  getCardConnectHostFailure,
  getAllCountriesRequest,
  getAllCountriesSuccess,
  getAllCountriesFailure,
  changePaymentMethodRequest,
  changePaymentMethodSuccess,
  changePaymentMethodFailure,
  resetChangePaymentMethodState,
  getUserActiveStatsRequest,
  getUserActiveStatsSuccess,
  getUserActiveStatsFailure,
  validateUpdateSubscriptionPlanRequest,
  validateUpdateSubscriptionPlanSuccess,
  validateUpdateSubscriptionPlanFailure,
  updateSubscriptionPlanRequest,
  updateSubscriptionPlanSuccess,
  updateSubscriptionPlanFailure,
  resetValidateUpdateSubscriptionPlan,
  resetUpdateSubscriptionPlan,
  subscriptionPlanPaymentRequest,
  subscriptionPlanPaymentSuccess,
  subscriptionPlanPaymentFailure,
  resetSubscriptionPlanPayment,
  getNewBillingInfoRequest,
  getNewBillingInfoSuccess,
  getNewBillingInfoFailure,
  resetNewBillingInfo,
} = mainSlice.actions;

export const mainReducer = mainSlice.reducer;
